import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Store, Plus, Loader2, Trash2, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isNativeIOS } from "@/lib/nativePush";
import { Link } from "react-router-dom";

export default function CreatorShopPage() {
  const { user } = useAuth();
  const canSell = !!(user && (user.is_creator || user.role === "admin"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", price_dollars: 9.99, image_url: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/creator-shop/items");
      setItems(data);
    } catch { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const price_cents = Math.round(Number(form.price_dollars) * 100);
    if (form.title.trim().length < 2) return toast.error("Title too short.");
    if (!(price_cents >= 99 && price_cents <= 20000)) return toast.error("Price must be $0.99 – $200.00");
    setBusy(true);
    try {
      await api.post("/creator-shop/items", {
        title: form.title.trim(),
        description: form.description.trim(),
        price_cents,
        image_url: form.image_url || null,
      });
      toast.success("Item listed.");
      setCreating(false);
      setForm({ title: "", description: "", price_dollars: 9.99, image_url: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not list."); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/creator-shop/items/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Could not delete."); }
  };

  const buy = async (item) => {
    if (isNativeIOS()) {
      toast.error("Purchases from the Creator Shop aren't available in the iOS app.");
      return;
    }
    try {
      const { data } = await api.post(`/creator-shop/items/${item.id}/checkout`, {
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Stripe did not return a checkout URL.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start checkout.";
      // Friendly nudge if the creator hasn't finished Stripe onboarding.
      if (/onboarding|payouts|connect/i.test(msg)) {
        toast.error(msg + " Ask the creator to finish payouts setup.");
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="creator-shop-root">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-1">Shop</div>
          <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
            <Store className="text-sky-400" size={28} /> Creator Shop
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Digital drops from the vault — photo sets, videos, 1:1 requests.</p>
        </div>
        {canSell && (
          <Button onClick={() => setCreating((v) => !v)} className="bg-sky-600 hover:bg-sky-500 text-white rounded-full h-9 px-4 flex items-center gap-2" data-testid="shop-new-toggle">
            <Plus size={14} /> {creating ? "Cancel" : "List item"}
          </Button>
        )}
      </header>

      {creating && canSell && (
        <div className="card-surface p-5 mb-5 space-y-3" data-testid="shop-form">
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value.slice(0, 80) }))} placeholder="Title (e.g. 'Exclusive photo set — August')" className="bg-black/40 border-white/10 rounded-none" data-testid="shop-title" />
          <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, 500) }))} placeholder="What do buyers get?" rows={2} className="bg-black/40 border-white/10 rounded-none" data-testid="shop-description" />
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs uppercase tracking-widest text-zinc-500">Price (USD)</label>
              <Input type="number" step="0.01" min="0.99" max="200" value={form.price_dollars} onChange={(e) => setForm((f) => ({ ...f, price_dollars: e.target.value }))} className="bg-black/40 border-white/10 rounded-none mt-1" data-testid="shop-price" />
            </div>
            <div className="flex-1">
              <label className="text-xs uppercase tracking-widest text-zinc-500">Image URL (optional)</label>
              <Input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="https://…" className="bg-black/40 border-white/10 rounded-none mt-1" data-testid="shop-image" />
            </div>
          </div>
          <Button onClick={create} disabled={busy} className="bg-sky-600 hover:bg-sky-500 text-white rounded-full w-full" data-testid="shop-submit">
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null} List for sale
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <div className="font-display text-2xl mb-2">No items yet.</div>
          <div className="text-sm text-zinc-500">
            {canSell ? "Tap 'List item' to publish your first drop." : "Check back soon — creators are stocking the shelves."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((it) => {
            const mine = user && it.creator_id === user.id;
            return (
              <div key={it.id} className="card-surface overflow-hidden" data-testid={`shop-item-${it.id}`}>
                {it.image_url ? (
                  <div className="relative aspect-video bg-black/50">
                    <img src={it.image_url} alt={it.title} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-sky-900/30 via-black to-black flex items-center justify-center text-6xl">🛍️</div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-base font-semibold leading-tight line-clamp-2">{it.title}</div>
                    <div className="text-amber-400 font-semibold text-lg shrink-0">${(it.price_cents / 100).toFixed(2)}</div>
                  </div>
                  {it.description && <div className="text-xs text-zinc-400 mt-2 line-clamp-2">{it.description}</div>}
                  <div className="flex items-center gap-2 mt-3">
                    <Link to={`/u/${it.creator?.id}`} className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80">
                      <Avatar className="w-7 h-7 border border-white/10">
                        <AvatarImage src={it.creator?.avatar_url} />
                        <AvatarFallback className="bg-rose-600 text-white text-[10px]">{it.creator?.display_name?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className={`text-xs truncate`}>{it.creator?.display_name}</span>
                    </Link>
                    {mine ? (
                      <Button onClick={() => remove(it.id)} variant="ghost" className="rounded-full h-8 px-3 text-xs border border-white/15" data-testid={`shop-delete-${it.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    ) : (
                      <Button onClick={() => buy(it)} className="bg-sky-600 hover:bg-sky-500 text-white rounded-full h-8 px-3 text-xs" data-testid={`shop-buy-${it.id}`}>
                        Buy <ExternalLink size={11} className="ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
